import { useState, useRef, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { processDateSort, processNumberSort } from 'utils/sort';
import { filterListingsByQuery } from 'utils/search';
import { ListingSortFilter } from 'components/Listing/types';
import { ListingModel } from 'models';

export function useListingFilterSort(
	listings: ListingModel[],
	initialSort: ListingSortFilter = ListingSortFilter.DEFAULT
) {
	const [rawQuery, setRawQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const debouncedSet = useRef(
		debounce((q: string) => setDebouncedQuery(q), 300)
	).current;

	const [selectedYears, setSelectedYears] = useState<number[]>([]);
	const [selectedPrices, setSelectedPrices] = useState<number[]>([]);
	const [showOriginal, setShowOriginal] = useState(false);
	const [showSet, setShowSet] = useState(false);
	const [sortKey, setSortKey] = useState<ListingSortFilter>(initialSort);
	const [sortType, setSortType] = useState<'asc' | 'desc'>('desc');
	const [displayFilters, setDisplayFilters] = useState(false);

	const onSearch = (q: string) => {
		setRawQuery(q);
		debouncedSet(q);
	};

	const hasFiltersApplied = () =>
		selectedYears.length === 2 ||
		selectedPrices.length === 2 ||
		showOriginal ||
		showSet;

	const isOnFilterMode = () =>
		debouncedQuery.length > 0 ||
		sortKey !== ListingSortFilter.DEFAULT ||
		hasFiltersApplied();

	const resetFilters = () => {
		setRawQuery('');
    setDebouncedQuery('');
    setSelectedYears([]);
    setSelectedPrices([]);
    setShowOriginal(false);
    setShowSet(false);
    setDisplayFilters(false);
	}

	const filteredListings = useMemo(() => {
		let newListings = listings.slice();

		if (debouncedQuery) {
			newListings = filterListingsByQuery(newListings, debouncedQuery);
		}

		if (selectedYears.length === 2) {
			const [minYear, maxYear] = selectedYears;
			newListings = newListings.filter(listing => {
				const year = listing.getDateYear();
				return year >= minYear && year <= maxYear;
			});
		}

		if (selectedPrices.length === 2) {
			const [minPrice, maxPrice] = selectedPrices;
			newListings = newListings.filter(listing => listing.rawPrice >= minPrice && listing.rawPrice <= maxPrice);
		}

		if (showOriginal) {
			newListings = newListings.filter(l => l.isOriginal());
		}
		if (showSet) {
			newListings = newListings.filter(l => l.isSet());
		}

		const isAscending: boolean = sortType === 'asc';
		switch (sortKey) {
			case ListingSortFilter.TITLE:
				newListings.sort((a, b) =>
					isAscending ? (a.name > b.name ? 1 : -1) : (a.name > b.name ? -1 : 1)
				);
				break;
			case ListingSortFilter.SALE_DATE:
				newListings.sort((a, b) => processDateSort(a.date, b.date, !isAscending));
				break;
			case ListingSortFilter.PRICE:
				newListings.sort((a, b) => processNumberSort(a.rawPrice, b.rawPrice, isAscending));
				break;
			default:
				break;
		}

		return newListings;
	}, [
		listings,
		debouncedQuery,
		selectedYears,
		selectedPrices,
		showOriginal,
		showSet,
		sortKey,
		sortType,
	]);

	return {
		rawQuery,
		onSearch,
		selectedYears,
		setSelectedYears,
		selectedPrices,
		setSelectedPrices,
		showOriginal,
		setShowOriginal,
		showSet,
		setShowSet,
		sortKey,
		setSortKey,
		sortType,
		setSortType,
		displayFilters,
		setDisplayFilters,
		filteredListings,
		hasFiltersApplied,
		isOnFilterMode,
		resetFilters
	};
}
